import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn.js';

export type ContextMenuEntry =
  | {
      label: string;
      icon?: ReactNode;
      onSelect: () => void;
      /** Red, for destructive actions. */
      danger?: boolean;
      disabled?: boolean;
      /** Right-aligned hint, e.g. a shortcut. */
      hint?: string;
    }
  /** A row of small buttons (e.g. quick reactions), shown at the top like Discord. */
  | { quick: { label: string; content: ReactNode; onSelect: () => void }[] }
  | 'separator';

/** Menu items; `false`/`null`/`undefined` are skipped so entries can be conditional. */
export type MenuEntries = (ContextMenuEntry | false | null | undefined)[];

const EDGE = 8;

/**
 * Right-click menu, like Discord's: `const menu = useContextMenu();` then
 * `<div onContextMenu={menu.open(entries)}>` and render `{menu.element}` once. Entries that are
 * `false`/`null` are skipped, so items can be listed conditionally. Arrow keys move, Enter picks,
 * Escape or a click elsewhere closes it.
 */
export function useContextMenu() {
  const [state, setState] = useState<{
    x: number;
    y: number;
    entries: ContextMenuEntry[];
  } | null>(null);
  const close = useCallback(() => setState(null), []);

  const open = useCallback(
    (entries: MenuEntries | (() => MenuEntries)) => (event: ReactMouseEvent) => {
      // Links, media, text fields and selected text keep the browser's own menu (open in a new
      // tab, copy, paste…), like Discord does.
      const native = (event.target as HTMLElement).closest(
        'a[href], img, video, input, textarea, [contenteditable="true"]',
      );
      // (The element with the menu may itself be a link, e.g. a channel in the sidebar.)
      if (native && native !== event.currentTarget) return;
      if (window.getSelection()?.toString()) return;
      const list = (typeof entries === 'function' ? entries() : entries).filter(
        (e): e is ContextMenuEntry => Boolean(e),
      );
      // Nothing to offer: leave the browser's menu.
      if (list.every((e) => e === 'separator')) return;
      event.preventDefault();
      event.stopPropagation();
      setState({ x: event.clientX, y: event.clientY, entries: list });
    },
    [],
  );

  /** Opens the menu next to an element (for a «⋯» button with the same actions). */
  const openAt = useCallback((element: HTMLElement, entries: MenuEntries) => {
    const rect = element.getBoundingClientRect();
    setState({
      x: rect.left,
      y: rect.bottom + 4,
      entries: entries.filter((e): e is ContextMenuEntry => Boolean(e)),
    });
  }, []);

  const element = state
    ? createPortal(
        <ContextMenuPanel x={state.x} y={state.y} entries={state.entries} onClose={close} />,
        document.body,
      )
    : null;

  return { open, openAt, close, element, isOpen: state !== null };
}

function ContextMenuPanel({
  x,
  y,
  entries,
  onClose,
}: {
  x: number;
  y: number;
  entries: ContextMenuEntry[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Keep it inside the window: flip left/up when it would stick out.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();
    const left = x + width > window.innerWidth - EDGE ? Math.max(EDGE, x - width) : x;
    const top = y + height > window.innerHeight - EDGE ? Math.max(EDGE, y - height) : y;
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    node.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }, [x, y]);

  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('blur', onClose);
    document.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('blur', onClose);
      document.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  function onKeyDown(event: ReactKeyboardEvent) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [
      ...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []),
    ];
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    items[(next + items.length) % items.length]?.focus();
  }

  return (
    <div
      ref={ref}
      role="menu"
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        // Portals bubble React events to the element that opened the menu.
        e.preventDefault();
        e.stopPropagation();
      }}
      style={{ left: x, top: y }}
      className="fixed z-[100] min-w-52 animate-fade-in rounded-xl border border-border bg-surface p-1.5 shadow-float"
    >
      {entries.map((entry, i) =>
        entry === 'separator' ? (
          <div key={`sep-${i}`} role="separator" className="my-1 h-px bg-border" />
        ) : 'quick' in entry ? (
          <div key={`quick-${i}`} className="flex gap-1 px-1 py-0.5">
            {entry.quick.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                title={item.label}
                aria-label={item.label}
                onClick={() => {
                  onClose();
                  item.onSelect();
                }}
                className="flex size-9 items-center justify-center rounded-lg bg-surface-2 text-lg outline-none hover:bg-accent-soft focus-visible:ring-2 focus-visible:ring-accent"
              >
                {item.content}
              </button>
            ))}
          </div>
        ) : (
          <button
            key={entry.label}
            type="button"
            role="menuitem"
            disabled={entry.disabled}
            onClick={() => {
              onClose();
              entry.onSelect();
            }}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm outline-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
              entry.danger
                ? 'text-danger hover:bg-danger hover:text-white focus-visible:bg-danger focus-visible:text-white'
                : 'hover:bg-accent hover:text-white focus-visible:bg-accent focus-visible:text-white',
            )}
          >
            {entry.icon}
            <span className="flex-1 truncate">{entry.label}</span>
            {entry.hint && <span className="text-xs opacity-60">{entry.hint}</span>}
          </button>
        ),
      )}
    </div>
  );
}
